import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Calendar, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Image as ImageIcon,
  Save, 
  Percent, 
  CheckCircle2,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function ContactCountdownForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const getAuthHeaders = useCallback((includeContentType = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (includeContentType) headers['Content-Type'] = 'application/json';
    return headers;
  }, []);

  const [formData, setFormData] = useState({
    kicker: 'Construction Updates',
    title: 'Our Campus Opens in August 2026!',
    description: 'Follow our progress as we build the next chapter for learning.',
    target_date: '2026-08-01T00:00:00',
    construction_image: '/assets/news/news5.jpg',
    construction_progress: 72,
    construction_status: 'Structural work completed. Interior finishing and landscape underway.',
    construction_next_milestone: 'Classroom setup, lab installations, and safety certifications.',
    construction_milestones: [
      'Foundation and structural frame completed',
      'Interior finishing in progress',
      'Lab installations scheduled',
      'Landscape and safety certification next'
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    target_date: false,
    construction_image: false,
    construction_progress: false,
    construction_status: false,
    construction_next_milestone: false,
    construction_milestones: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [previewModalImg, setPreviewModalImg] = useState(null);

  // Live countdown calculation for UI preview
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders(false)
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          const map = {};
          const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
          json.data.forEach(m => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[ContactCountdownForm] Media map fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  useEffect(() => {
    const calcTime = () => {
      try {
        const target = new Date(formData.target_date || '2026-08-01T00:00:00');
        const now = new Date();
        const diff = Math.max(target.getTime() - now.getTime(), 0);
        const totalSecs = Math.floor(diff / 1000);
        const days = Math.floor(totalSecs / 86400);
        const hours = Math.floor((totalSecs % 86400) / 3600);
        const minutes = Math.floor((totalSecs % 3600) / 60);
        const seconds = totalSecs % 60;
        setTimeRemaining({ days, hours, minutes, seconds });
      } catch (e) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    calcTime();
    const timer = setInterval(calcTime, 1000);
    return () => clearInterval(timer);
  }, [formData.target_date]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=contact-us`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'contact-countdown' || s.section_type === 'countdown');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'contact-countdown' || s.section_type === 'countdown');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        const rawContent = foundSection.content_published || foundSection.content;
        let parsed = rawContent;
        if (typeof rawContent === 'string') {
          try {
            parsed = JSON.parse(rawContent);
          } catch (e) {
            console.error('Failed to parse contact-countdown content', e);
          }
        }

        const dataEn = parsed?.en || parsed || {};
        const newFormData = {
          kicker: dataEn.kicker || 'Construction Updates',
          title: dataEn.title || 'Our Campus Opens in August 2026!',
          description: dataEn.description || 'Follow our progress as we build the next chapter for learning.',
          target_date: dataEn.target_date || '2026-08-01T00:00:00',
          construction_image: dataEn.construction_image || '/assets/news/news5.jpg',
          construction_progress: dataEn.construction_progress !== undefined ? Number(dataEn.construction_progress) : 72,
          construction_status: dataEn.construction_status || 'Structural work completed. Interior finishing and landscape underway.',
          construction_next_milestone: dataEn.construction_next_milestone || 'Classroom setup, lab installations, and safety certifications.',
          construction_milestones: Array.isArray(dataEn.construction_milestones) ? dataEn.construction_milestones : [
            'Foundation and structural frame completed',
            'Interior finishing in progress',
            'Lab installations scheduled',
            'Landscape and safety certification next'
          ]
        };

        setFormData(newFormData);
        setOriginalData(newFormData);

        // Fetch pending changes
        if (foundSection.id) {
          try {
            const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
              credentials: 'include',
              headers: getAuthHeaders()
            });
            if (pendRes.ok) {
              const pendJson = await pendRes.json();
              if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
                const pendMap = {};
                pendJson.data.pending_changes.forEach(change => {
                  if (change.status === 'pending') {
                    pendMap[change.field_id] = true;
                  }
                });
                setPendingFields(pendMap);
              }
            }
          } catch (err) {
            console.warn('Could not load pending changes for contact-countdown', err);
          }
        }
      }
    } catch (err) {
      console.error('Error loading contact-countdown section:', err);
      toast.error('Failed to load contact countdown data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMilestoneChange = (index, value) => {
    setFormData(prev => {
      const updated = [...prev.construction_milestones];
      updated[index] = value;
      return { ...prev, construction_milestones: updated };
    });
  };

  const handleAddMilestone = () => {
    setFormData(prev => ({
      ...prev,
      construction_milestones: [...prev.construction_milestones, 'Milestone tahap baru...']
    }));
    toast.info('Milestone baru ditambahkan');
  };

  const handleRemoveMilestone = (index) => {
    if (formData.construction_milestones.length <= 1) {
      toast.warning('Minimal harus ada 1 poin milestone');
      return;
    }
    setFormData(prev => ({
      ...prev,
      construction_milestones: prev.construction_milestones.filter((_, idx) => idx !== index)
    }));
    toast.info('Milestone dihapus');
  };

  const handleImageUpload = (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }

    setImageUploading(true);
    setImageProgress(10);

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('category', 'construction');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setImageProgress(pct);
      }
    };

    xhr.onload = () => {
      setImageUploading(false);
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let photoUrl = '';
          if (data.url) photoUrl = data.url;
          else if (data.file_path) photoUrl = data.file_path;
          else if (res.url) photoUrl = res.url;
          else if (res.file_path) photoUrl = res.file_path;

          if (photoUrl && !photoUrl.startsWith('http://') && !photoUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            photoUrl = `${origin}${photoUrl.startsWith('/') ? '' : '/'}${photoUrl}`;
          }
          if (photoUrl) {
            handleInputChange('construction_image', photoUrl);
            toast.success('Foto progres konstruksi berhasil diunggah!');
          } else {
            toast.error('Gagal membaca URL foto');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload foto gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(false);
      toast.error('Koneksi terputus saat upload foto');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (fieldId, proposedValue, currentVal, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section not found in database. Please run migrations first.');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const payload = {
        section_id: sectionId,
        field_id: fieldId,
        field_name: fieldDisplayName || (fieldId.replace(/_/g, ' ').toUpperCase()),
        current_value: typeof currentVal === 'object' ? JSON.stringify(currentVal) : String(currentVal || ''),
        proposed_value: typeof proposedValue === 'object' ? JSON.stringify(proposedValue) : String(proposedValue || ''),
        old_value: currentVal !== undefined ? currentVal : originalData[fieldId],
        new_value: proposedValue !== undefined ? proposedValue : formData[fieldId],
        locale: 'en',
        language: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success(`"${fieldDisplayName || fieldId}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        setOriginalData(prev => ({ ...prev, [fieldId]: proposedValue }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit field error:', err);
      toast.error(err.message || 'Failed to submit changes');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleSubmitMilestones = async () => {
    if (!sectionId) {
      toast.error('Section not found in database');
      return;
    }

    setSubmitting(prev => ({ ...prev, milestones: true }));
    try {
      const payload = {
        section_id: sectionId,
        field_id: 'construction_milestones',
        field_name: 'Daftar Milestones Pembangunan Kampus',
        current_value: JSON.stringify(originalData.construction_milestones),
        proposed_value: JSON.stringify(formData.construction_milestones),
        old_value: originalData.construction_milestones,
        new_value: formData.construction_milestones,
        locale: 'en',
        language: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success('Daftar tahapan milestone berhasil diajukan untuk approval');
        setPendingFields(prev => ({ ...prev, construction_milestones: true }));
        setOriginalData(prev => ({ ...prev, construction_milestones: formData.construction_milestones }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit milestones error:', err);
      toast.error(err.message || 'Failed to submit milestones');
    } finally {
      setSubmitting(prev => ({ ...prev, milestones: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Campus Construction Section...</p>
      </div>
    );
  }

  const imagePreviewSrc = resolveImage(formData.construction_image);
  const isMilestonesModified = JSON.stringify(formData.construction_milestones) !== JSON.stringify(originalData.construction_milestones);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {previewModalImg && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={() => setPreviewModalImg(null)}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#fff',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 18px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Preview Foto Progres Konstruksi
              </span>
              <button 
                type="button" 
                onClick={() => setPreviewModalImg(null)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '16px', textAlign: 'center', overflow: 'auto', background: '#0f172a' }}>
              <img 
                src={previewModalImg} 
                alt="Construction Preview" 
                style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} 
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Standard Unified Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Calendar size={22} />
        </div>
        <div>
          <h2 className="unified-title">Campus Construction & Grand Opening</h2>
          <p className="unified-subtitle">
            Kelola tanggal target peresmian kampus baru, persentase progres pembangunan gedung, foto lapangan terkini, dan daftar pencapaian proyek.
          </p>
        </div>
        {sectionId && (
          <span className="unified-section-badge">ID: {sectionId}</span>
        )}
      </div>

      <div className="unified-form-body">
        {/* Live Countdown Preview Widget */}
        <div className="unified-countdown-preview-box">
          <div className="unified-countdown-preview-header">
            <span className="unified-countdown-pulse" />
            <strong>Live Preview Countdown Timer Menuju Tanggal Target</strong>
          </div>
          <div className="unified-countdown-digits-grid">
            <div className="unified-digit-card">
              <span className="unified-digit-num">{timeRemaining.days}</span>
              <span className="unified-digit-lbl">Hari (Days)</span>
            </div>
            <div className="unified-digit-card">
              <span className="unified-digit-num">{String(timeRemaining.hours).padStart(2, '0')}</span>
              <span className="unified-digit-lbl">Jam (Hours)</span>
            </div>
            <div className="unified-digit-card">
              <span className="unified-digit-num">{String(timeRemaining.minutes).padStart(2, '0')}</span>
              <span className="unified-digit-lbl">Menit (Mins)</span>
            </div>
            <div className="unified-digit-card">
              <span className="unified-digit-num">{String(timeRemaining.seconds).padStart(2, '0')}</span>
              <span className="unified-digit-lbl">Detik (Secs)</span>
            </div>
          </div>
        </div>

        {/* Target Opening Date */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Target Tanggal & Jam Pembukaan Kampus</label>
              <p className="unified-hint">Waktu target peresmian yang akan dihitung mundur otomatis oleh sistem di halaman publik.</p>
            </div>
            {pendingFields.target_date && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="datetime-local"
              className="unified-input"
              value={formData.target_date ? formData.target_date.substring(0, 16) : '2026-08-01T00:00'}
              onChange={(e) => handleInputChange('target_date', e.target.value)}
              disabled={pendingFields.target_date}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('target_date', formData.target_date, originalData.target_date, 'Target Opening Date')}
              disabled={submitting.target_date || pendingFields.target_date || formData.target_date === originalData.target_date}
            >
              <Save size={14} /> {submitting.target_date ? 'Saving...' : (pendingFields.target_date ? 'Pending' : 'Save Target Date')}
            </button>
          </div>
        </div>

        {/* Kicker and Title Grid */}
        <div className="unified-grid-2">
          {/* Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Kicker</label>
              {pendingFields.kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleInputChange('kicker', e.target.value)}
                disabled={pendingFields.kicker}
                placeholder="e.g. Construction Updates"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('kicker', formData.kicker, originalData.kicker, 'Section Kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Headline Title</label>
              {pendingFields.title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                disabled={pendingFields.title}
                placeholder="e.g. Our Campus Opens in August 2026!"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('title', formData.title, originalData.title, 'Headline Title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Description Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Deskripsi Pengantar Proyek</label>
              <p className="unified-hint">Penjelasan ringkas pesan pembuka progres kampus bagi orang tua dan calon murid.</p>
            </div>
            {pendingFields.description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={pendingFields.description}
              placeholder="Follow our progress as we build the next chapter for learning."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('description', formData.description, originalData.description, 'Project Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* Construction Progress Percentage */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Progres Konstruksi Fisik: {formData.construction_progress}%</label>
              <p className="unified-hint">Geser slider atau ketik persentase penyelesaian proyek (0% s/d 100%).</p>
            </div>
            {pendingFields.construction_progress && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-progress-slider-row">
            <input
              type="range"
              min="0"
              max="100"
              className="unified-slider"
              value={formData.construction_progress}
              onChange={(e) => handleInputChange('construction_progress', Number(e.target.value))}
              disabled={pendingFields.construction_progress}
            />
            <div className="unified-progress-input-badge">
              <input
                type="number"
                min="0"
                max="100"
                className="unified-input-small"
                value={formData.construction_progress}
                onChange={(e) => handleInputChange('construction_progress', Math.min(100, Math.max(0, Number(e.target.value))))}
                disabled={pendingFields.construction_progress}
              />
              <Percent size={14} />
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('construction_progress', formData.construction_progress, originalData.construction_progress, 'Progress Percentage')}
              disabled={submitting.construction_progress || pendingFields.construction_progress || formData.construction_progress === originalData.construction_progress}
            >
              <Save size={14} /> {submitting.construction_progress ? 'Saving...' : (pendingFields.construction_progress ? 'Pending' : 'Save Progress')}
            </button>
          </div>
        </div>

        {/* Construction Photo Upload */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Foto Lapangan / Banner Konstruksi Terkini</label>
              <p className="unified-hint">Foto visual progres pembangunan gedung yang tampil berdampingan dengan timer countdown.</p>
            </div>
            {pendingFields.construction_image && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>

          <div className="unified-banner-media-container">
            <div 
              className="unified-banner-media-preview"
              style={{ cursor: imagePreviewSrc ? 'pointer' : 'default', position: 'relative' }}
              onClick={() => {
                if (imagePreviewSrc) setPreviewModalImg(imagePreviewSrc);
              }}
              title={imagePreviewSrc ? 'Klik untuk melihat preview ukuran penuh' : ''}
            >
              {imagePreviewSrc ? (
                <>
                  <img src={imagePreviewSrc} alt="Construction" className="unified-banner-preview-img" />
                  <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', borderRadius: '6px', padding: '3px 6px', color: '#fff', display: 'flex', alignItems: 'center' }}>
                    <Eye size={12} />
                  </div>
                </>
              ) : (
                <div className="unified-banner-placeholder">
                  <ImageIcon size={32} />
                  <span>Belum ada foto</span>
                </div>
              )}
            </div>

            <div className="unified-banner-media-controls">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label className="unified-secondary-btn" style={{ cursor: imageUploading ? 'not-allowed' : 'pointer', alignSelf: 'flex-start' }}>
                  <Upload size={14} />
                  <span>{imageUploading ? `Mengunggah (${imageProgress}%)...` : 'Unggah Foto Baru'}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(e.target.files?.[0])}
                    disabled={pendingFields.construction_image || imageUploading}
                  />
                </label>

                {imagePreviewSrc && (
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    style={{ padding: '8px 14px', fontSize: '13px' }}
                    onClick={() => setPreviewModalImg(imagePreviewSrc)}
                  >
                    <Eye size={14} /> Lihat Foto Penuh
                  </button>
                )}
              </div>

              <div className="unified-form-group" style={{ marginTop: '14px' }}>
                <label className="unified-sub-label">Image Path / URL Foto:</label>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.construction_image || ''}
                    onChange={(e) => handleInputChange('construction_image', e.target.value)}
                    placeholder="/assets/news/news5.jpg"
                    disabled={pendingFields.construction_image}
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitField('construction_image', formData.construction_image, originalData.construction_image, 'Construction Photo')}
                    disabled={submitting.construction_image || pendingFields.construction_image || formData.construction_image === originalData.construction_image}
                  >
                    <Save size={14} /> {submitting.construction_image ? 'Saving...' : (pendingFields.construction_image ? 'Pending' : 'Save Photo')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Current Status and Next Milestone Grid */}
        <div className="unified-grid-2">
          {/* Current Status */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Status Pengerjaan Saat Ini</label>
              {pendingFields.construction_status && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-column">
              <textarea
                className="unified-textarea"
                rows={2}
                value={formData.construction_status}
                onChange={(e) => handleInputChange('construction_status', e.target.value)}
                disabled={pendingFields.construction_status}
                placeholder="Structural work completed..."
              />
              <div className="unified-align-right">
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={() => handleSubmitField('construction_status', formData.construction_status, originalData.construction_status, 'Current Status')}
                  disabled={submitting.construction_status || pendingFields.construction_status || formData.construction_status === originalData.construction_status}
                >
                  <Save size={14} /> {submitting.construction_status ? 'Saving...' : (pendingFields.construction_status ? 'Pending' : 'Save Status')}
                </button>
              </div>
            </div>
          </div>

          {/* Next Milestone */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Target Milestone Berikutnya</label>
              {pendingFields.construction_next_milestone && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-column">
              <textarea
                className="unified-textarea"
                rows={2}
                value={formData.construction_next_milestone}
                onChange={(e) => handleInputChange('construction_next_milestone', e.target.value)}
                disabled={pendingFields.construction_next_milestone}
                placeholder="Classroom setup, lab installations..."
              />
              <div className="unified-align-right">
                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={() => handleSubmitField('construction_next_milestone', formData.construction_next_milestone, originalData.construction_next_milestone, 'Next Milestone')}
                  disabled={submitting.construction_next_milestone || pendingFields.construction_next_milestone || formData.construction_next_milestone === originalData.construction_next_milestone}
                >
                  <Save size={14} /> {submitting.construction_next_milestone ? 'Saving...' : (pendingFields.construction_next_milestone ? 'Pending' : 'Save Milestone')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Milestone Steps List Checklist */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Daftar Poin Milestone Pembangunan ({formData.construction_milestones.length} Tahap)</label>
              <p className="unified-hint">Daftar butir checklist tahap penyelesaian yang tampil di kartu progres.</p>
            </div>
            {pendingFields.construction_milestones && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>

          <div className="unified-sub-list">
            {formData.construction_milestones.map((milestone, mIdx) => (
              <div key={mIdx} className="unified-list-item-row">
                <div style={{ display: 'flex', alignItems: 'center', color: '#2563eb' }}>
                  <CheckCircle2 size={16} />
                </div>
                <input
                  type="text"
                  className="unified-input"
                  value={milestone}
                  onChange={(e) => handleMilestoneChange(mIdx, e.target.value)}
                  disabled={pendingFields.construction_milestones}
                  placeholder={`Tahap ${mIdx + 1}...`}
                />
                <button
                  type="button"
                  className="unified-danger-icon-btn"
                  onClick={() => handleRemoveMilestone(mIdx)}
                  disabled={pendingFields.construction_milestones}
                  title="Hapus milestone"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <div className="unified-multi-action-bar">
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={handleAddMilestone}
              disabled={pendingFields.construction_milestones}
            >
              <Plus size={14} /> Tambah Milestone
            </button>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleSubmitMilestones}
              disabled={submitting.milestones || pendingFields.construction_milestones || !isMilestonesModified}
            >
              <Save size={14} />
              {submitting.milestones ? 'Menyimpan...' : (pendingFields.construction_milestones ? 'Pending Approval' : 'Simpan Seluruh Milestone')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
